import api from '.';
import logger from '../utils/logger';
import { ApiConfig } from '../types/common';
import { AxiosError } from 'axios';
import { handleApiError } from '../utils/rental';
import { Order, ORDER_BY, OrderResponse, SORT_BY } from '../types/order';
import { OrderContract, ShowActiveRentalItemDTO, ShowOrderItemDTO } from '../types/order-item';
import {
  OffRentOrderData,
  OffRentOrderDataUpdateNotes,
  RentalNoteResponse,
} from '../types/offrent-types';
import {
  AddPurchaseOrderFromModal,
  AttachPurchaseOrderToRental,
  CreatePurchaseOrderResponse,
  PurchaseOrderDetails,
  UpdatePurchaseOrderFromModal,
} from '../types/purchase-order';

export const getOrder = async (orderId: string): Promise<Order> => {
  try {
    const { data } = await api.get(`/rental-order-service/rental/request/${orderId}`);
    return data;
  } catch (error) {
    logger('error', 'at getOrder while fetching order request details');
    throw new Error('error at getOrder while fetching order request details');
  }
};

export const postOrder = async (order: Order, recaptchaToken: string): Promise<OrderResponse> => {
  try {
    const requestBody = {
      order,
      recaptchaToken,
    };
    const { data } = await api.post('/rental-order-service/rental/request', requestBody);
    return data;
  } catch (error) {
    logger('error', 'at postOrder while posting order request');
    handleApiError((error as AxiosError)?.response?.data, 'error at postOrder while posting order request');
  }
};

export const getAllOrders = async (page: number, limit: number): Promise<ShowOrderItemDTO[]> => {
  try {
    const { data } = await api.get(
      `/rental-order-service/rental-requests?page=${page}&limit=${limit}`,
    );
    return data;
  } catch (error) {
    logger('error', 'at getAllOrders while fetching order request details');
    throw new Error('error at getAllOrders while fetching order request details');
  }
};

export const getAllActiveRentals = async (
  companyId: number | undefined,
  config?: ApiConfig,
  sortBy: SORT_BY = SORT_BY.START_DATE,
  orderBy: ORDER_BY = ORDER_BY.DESCENDING,
): Promise<ShowActiveRentalItemDTO[]> => {
  try {
    if (!companyId)
      throw new Error('Undefined company id passed while fetching active rentals details');

    let url = `/companies/${companyId}/rentals?rental_status_id=5&has_assigned_assets=false&parts_information=true&sort_by=${sortBy}&order_by=${orderBy}&rental_type_id=1`;

    if (config) {
      const { page, size } = config;
      if (size) url += `&size=${size}`;
      if (page) url += `&page=${page}`;
    }

    const { data } = await api.get(url, {
      headers: {
        'X-Fields':
          '{current_location, equipment_class{equipment_class_id, name}, end_date, equipment{asset_id, category, category_id, custom_name, equipment_class{category_id, description, equipment_class_id, name}, equipment_class_id, equipment_make_id, make, model, name, photo}, off_rent_date_requested, order{order_id, purchase_order, market_id}, rental_id, rental_status, start_date, rental_type_id, part_assignments}',
      },
    });

    return data;
  } catch (error) {
    logger('error', 'at getAllActiveRentals while fetching active rentals details');
    throw new Error('error at getAllActiveRentals while fetching active rentals details');
  }
};

export const getActiveRental = async (
  rentalId: number | string | undefined,
): Promise<ShowActiveRentalItemDTO> => {
  try {
    if (!rentalId)
      throw new Error('Undefined rental id passed while fetching active rental detail');

    const url = `/rentals/${rentalId}`;

    const { data } = await api.get(url, {
      headers: {
        'X-Fields':
          '{current_location,end_date,equipment{asset_id, category, category_id, custom_name, equipment_class{category_id,description,equipment_class_id, name},equipment_class_id,equipment_make_id,make, model,name,photo,}off_rent_date_requested,order{order_id,purchase_order,market_id}rental_id,rental_status,start_date,rental_type_id}',
      },
    });

    return data;
  } catch (error) {
    logger('error', 'at getActiveRental while fetching active rental details');
    throw new Error('error at getActiveRental while fetching active rental details');
  }
};

export const getOrderContracts = async (orderId: number): Promise<OrderContract> => {
  try {
    const url = `/orders/${orderId}/contracts`;

    const { data } = await api.get(url);

    return data[0];
  } catch (error) {
    logger('error', 'at getOrderContracts while fetching order contracts details');
    throw new Error('error at getOrderContracts while fetching order contracts details');
  }
};

export const getContract = async (contractId: string, suffix: string): Promise<Blob> => {
  try {
    const url = `/contracts/${contractId}.${suffix}`;

    const { data } = await api.get(url, {
      responseType: 'blob',
    });

    return data;
  } catch (error) {
    logger('error', 'at getContract while fetching contract details');
    throw new Error('error at getContract while fetching contract details');
  }
};
export const addPurchaseOrder = async (
  companyId: string,
  addPurchaseOrderData: AddPurchaseOrderFromModal,
): Promise<CreatePurchaseOrderResponse> => {
  try {
    const { data } = await api.post(
      `/companies/${companyId}/purchase-orders`,
      addPurchaseOrderData,
    );
    return data;
  } catch (error) {
    logger('error', 'at addPurchaseOrder while adding order purchase details');
    throw error;
  }
};

export const attachPurchaseOrderToRental = async (
  companyId: string,
  rentalId: string,
  attachPurchaseOrderToRental: AttachPurchaseOrderToRental,
): Promise<CreatePurchaseOrderResponse> => {
  try {
    const { data } = await api.patch(
      `/companies/${companyId}/rentals/${rentalId}`,
      attachPurchaseOrderToRental,
    );
    return data;
  } catch (error) {
    logger('error', 'at attachPurchaseOrderToRental while adding order purchase details');
    throw new Error('error at attachPurchaseOrderToRental while adding order purchase details');
  }
};

export const updatePurchaseOrder = async (
  companyId: string,
  poNumber: string,
  updatepurchaseOrderData: UpdatePurchaseOrderFromModal,
): Promise<PurchaseOrderDetails> => {
  try {
    const { data } = await api.patch(
      `/companies/${companyId}/purchase-orders/${poNumber}`,
      updatepurchaseOrderData,
    );
    return data;
  } catch (error) {
    logger('error', 'at updatePurchaseOrder while updating order purchase details');
    throw new Error('error at updatePurchaseOrder while updating order purchase details');
  }
};

export const offRentOrder = async (
  companyId: string,
  rentalId: string,
  offRentOrderData: OffRentOrderData,
): Promise<ShowActiveRentalItemDTO> => {
  try {
    const { data } = await api.patch(
      `/companies/${companyId}/rentals/${rentalId}`,
      offRentOrderData,
    );
    return data;
  } catch (error) {
    logger('error', 'at offRentOrder while updating off rent order details');
    throw new Error('error at offRentOrder while updating off rent order details');
  }
};

export const offRentOrderNotesUpdate = async (
  rentalId: string,
  offRentOrderDataUpdateNotes: OffRentOrderDataUpdateNotes,
): Promise<RentalNoteResponse> => {
  try {
    const { data } = await api.post(`/rentals/${rentalId}/notes`, offRentOrderDataUpdateNotes);
    return data;
  } catch (error) {
    logger('error', 'at offRentOrderNotesUpdate while updating off rent order notes details');
    throw new Error('error at offRentOrderNotesUpdate while updating off rent order notes details');
  }
};

export const cancelScheduleOffrent = async (rentalId: string): Promise<void> => {
  try {
    const { data } = await api.patch(`/rentals/${rentalId}/cancel-off-rent`);
    return data;
  } catch (error) {
    logger('error', 'at cancelScheduleOffrent while canceling schedule offrent');
    throw new Error('error at cancelScheduleOffrent while canceling schedule offrent');
  }
};
